export type PlaybackViewMode = 'clip' | 'playback';

export type PlaybackAnchorMode = 'center' | 'start' | 'end';

export const PLAYBACK_CLIP_DURATION_SECONDS = 2;
export const PLAYBACK_DEFAULT_RANGE_SECONDS = 15;
/** Shared cameras datetime window used across process-event / site-check / patrol. */
export const CAMERAS_PLAYBACK_RANGE_SECONDS = 15;

export interface PlaybackWindow {
  start: Date;
  end: Date;
  durationSeconds: number;
  modeLabel: 'CLIP' | 'PLAYBACK';
}

/** Parses alarm timestamps such as `03/27/2026 11:00:01 CT` or `3/19/2026 11:00:00 CT`. */
export function parseEventAlarmTimestamp(value: string | undefined | null): Date | null {
  if (!value?.trim()) {
    return null;
  }

  const cleaned = value.replace(/\s+[A-Z]{2,4}$/i, '').trim();
  const match = cleaned.match(
    /^(\d{1,2})\/(\d{1,2})\/(\d{4})\s+(\d{1,2}):(\d{2}):(\d{2})$/,
  );
  if (!match) {
    const instant = new Date(value);
    return Number.isNaN(instant.getTime()) ? null : instant;
  }

  const first = Number(match[1]);
  const second = Number(match[2]);
  const year = Number(match[3]);
  const hours = Number(match[4]);
  const minutes = Number(match[5]);
  const seconds = Number(match[6]);

  let day: number;
  let month: number;
  if (first > 12) {
    day = first;
    month = second;
  } else {
    // Canonical display format is MM/DD/YYYY; also covers unambiguous month-first (e.g. 3/19).
    month = first;
    day = second;
  }

  const parsed = new Date(year, month - 1, day, hours, minutes, seconds);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

export function computePlaybackWindow(
  anchor: Date,
  mode: PlaybackViewMode,
  customRangeSeconds: number | null,
  anchorMode: PlaybackAnchorMode = 'center',
): PlaybackWindow {
  if (mode === 'clip') {
    const halfMs = (PLAYBACK_CLIP_DURATION_SECONDS * 1000) / 2;
    return {
      start: new Date(anchor.getTime() - halfMs),
      end: new Date(anchor.getTime() + halfMs),
      durationSeconds: PLAYBACK_CLIP_DURATION_SECONDS,
      modeLabel: 'CLIP',
    };
  }

  const totalSeconds = customRangeSeconds ?? PLAYBACK_DEFAULT_RANGE_SECONDS;
  if (anchorMode === 'start') {
    return {
      start: anchor,
      end: new Date(anchor.getTime() + totalSeconds * 1000),
      durationSeconds: totalSeconds,
      modeLabel: 'PLAYBACK',
    };
  }

  if (anchorMode === 'end') {
    return {
      start: new Date(anchor.getTime() - totalSeconds * 1000),
      end: anchor,
      durationSeconds: totalSeconds,
      modeLabel: 'PLAYBACK',
    };
  }

  const halfMs = (totalSeconds * 1000) / 2;
  return {
    start: new Date(anchor.getTime() - halfMs),
    end: new Date(anchor.getTime() + halfMs),
    durationSeconds: totalSeconds,
    modeLabel: 'PLAYBACK',
  };
}

export function formatPlaybackDuration(totalSeconds: number): string {
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
}

export function formatEventDateTimeDisplay(date: Date): string {
  const pad = (value: number) => String(value).padStart(2, '0');
  return `${pad(date.getMonth() + 1)}/${pad(date.getDate())}/${date.getFullYear()} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())} CT`;
}

/** Offset into the preview media where the alarm anchor sits for the active window. */
export function previewStartOffsetSeconds(
  window: PlaybackWindow,
  anchorMode: PlaybackAnchorMode = 'center',
): number {
  if (anchorMode === 'start' || window.durationSeconds <= 0) {
    return 0;
  }

  return window.durationSeconds / 2;
}

/** Ascending compare for alarm timestamp strings (`27/03/2026 11:00:01 CT`). */
export function compareEventAlarmTimestamps(
  left: string | null | undefined,
  right: string | null | undefined,
): number {
  const leftDate = parseEventAlarmTimestamp(left);
  const rightDate = parseEventAlarmTimestamp(right);
  if (leftDate && rightDate) {
    return leftDate.getTime() - rightDate.getTime();
  }
  if (leftDate) {
    return -1;
  }
  if (rightDate) {
    return 1;
  }
  return String(left ?? '').localeCompare(String(right ?? ''));
}

export interface AlarmTimestampRef {
  id: string;
  timestamp: string;
  cameraId?: string;
  camera?: string;
}

function alarmsNewestLast<T extends AlarmTimestampRef>(alarms: readonly T[]): T[] {
  return [...alarms].sort((left, right) => {
    const byTime = compareEventAlarmTimestamps(left.timestamp, right.timestamp);
    return byTime !== 0 ? byTime : left.id.localeCompare(right.id);
  });
}

/** Earliest alarm by parsed timestamp (stable for equal times). */
export function findEarliestAlarm<T extends AlarmTimestampRef>(alarms: readonly T[]): T | null {
  if (!alarms.length) {
    return null;
  }

  return alarmsNewestLast(alarms)[0] ?? null;
}

/** Newest alarm by parsed timestamp (stable for equal times). */
export function findLatestAlarm<T extends AlarmTimestampRef>(alarms: readonly T[]): T | null {
  if (!alarms.length) {
    return null;
  }

  return alarmsNewestLast(alarms).at(-1) ?? null;
}

/** Earliest alarm for a specific camera id. */
export function findEarliestAlarmForCamera<T extends AlarmTimestampRef>(
  alarms: readonly T[],
  cameraId: string,
): T | null {
  return findEarliestAlarm(alarms.filter((alarm) => alarm.cameraId === cameraId));
}

export function parseIsoTimestamp(value: string | null | undefined): Date | null {
  if (!value?.trim()) {
    return null;
  }
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

/** Local ISO string for mock segment fixtures (matches `parseEventAlarmTimestamp` local dates). */
export function formatLocalIsoTimestamp(date: Date): string {
  const pad = (value: number) => String(value).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}.000`;
}

/** Builds mock segment bounds centered on an alarm timestamp. */
export function segmentBoundsForAlarm(
  alarmTimestamp: string,
  paddingSeconds = 45,
): { start: string; end: string; anchor: Date } | null {
  const anchor = parseEventAlarmTimestamp(alarmTimestamp);
  if (!anchor) {
    return null;
  }

  return {
    anchor,
    start: formatLocalIsoTimestamp(new Date(anchor.getTime() - paddingSeconds * 1000)),
    end: formatLocalIsoTimestamp(new Date(anchor.getTime() + paddingSeconds * 1000)),
  };
}
